import React from "react";
import { Formik } from "formik";
import { CForm, CRow, CCol, CButton, CSpinner } from "@coreui/react";
import CustomInput from "@/components/CustomInput";
import { serviceCategory } from "@/constants/translation/serviceCategory.translation";
import { useTranslationContext } from "@/context/TranslationContext";
import { useNavigate } from "react-router-dom";
import { masters } from "@/libs/routes";
import constants from "@/libs/constant";

/**
 * ServiceCategoryForm Component
 * @param {Object} props - Component props
 * @param {Object} props.initialValues - Initial form values ({ name, name_de })
 * @param {Function} props.onSubmit - Function to handle form submission
 * @param {boolean} props.isSubmitting - Loading state for the submit button
 * @param {Object} [props.validationSchema] - Form validation schema
 * @param {boolean} [props.isEdit] - Whether the form is used for editing
 * @returns {JSX.Element} React component for the service category add/edit form
 */
const ServiceCategoryForm = ({ initialValues, onSubmit, isSubmitting, validationSchema, isEdit = false }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, handleChange, handleBlur, errors, touched }) => (
        <CForm
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <CRow className="mb-2 m-auto p-3 border rounded">
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(serviceCategory.form.name_label)}
                type={constants.inputTypes.text}
                name="name"
                placeholder={t(serviceCategory.form.name_placeholder)}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && errors.name}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(serviceCategory.form.name_de_label)}
                type={constants.inputTypes.text}
                name="name_de"
                placeholder={t(serviceCategory.form.name_de_placeholder)}
                value={values.name_de}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name_de && errors.name_de}
              />
            </CCol>
          </CRow>

          <CRow className="m-auto p-3">
            <CCol className="d-flex gap-2 justify-content-center">
              <CButton type="submit" color="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    {t(serviceCategory.form.loading_btn_text)}
                  </>
                ) : isEdit ? (
                  t(serviceCategory.form.update_btn_text)
                ) : (
                  t(serviceCategory.form.submit_btn_text)
                )}
              </CButton>
              <CButton
                type="button"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => navigate(masters.serviceCategory.list)}
              >
                {t(serviceCategory.form.cancel_btn_text)}
              </CButton>
            </CCol>
          </CRow>
        </CForm>
      )}
    </Formik>
  );
};

export default ServiceCategoryForm;
